import { useEffect } from 'react';

/**
 * Hook to disable pull-to-refresh on mobile PWA
 * Prevents accidental page reloads when scrolling
 */
export const useDisablePullToRefresh = () => {
  useEffect(() => {
    // Prevent pull-to-refresh
    let lastTouchY = 0;
    let preventPullToRefresh = false;

    const touchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      lastTouchY = e.touches[0].clientY;
      
      // Check if at top of page
      preventPullToRefresh = window.scrollY === 0;
    };

    const touchMove = (e: TouchEvent) => {
      if (!preventPullToRefresh) return;
      
      const touchY = e.touches[0].clientY;
      const touchYDelta = touchY - lastTouchY;
      lastTouchY = touchY;

      // Prevent pull-to-refresh if scrolling down when at top
      if (touchYDelta > 0) {
        e.preventDefault();
        return false;
      }
    };

    // Prevent overscroll
    const preventOverscroll = (e: Event) => {
      e.preventDefault();
    };

    document.addEventListener('touchstart', touchStart, { passive: false });
    document.addEventListener('touchmove', touchMove, { passive: false });
    document.addEventListener('overscroll', preventOverscroll);
    
    // Prevent context menu on long press
    document.addEventListener('contextmenu', (e) => {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        e.preventDefault();
      }
    });

    return () => {
      document.removeEventListener('touchstart', touchStart);
      document.removeEventListener('touchmove', touchMove);
      document.removeEventListener('overscroll', preventOverscroll);
    };
  }, []);
};

/**
 * Hook to persist and restore auth state
 * Prevents logout on page refresh in PWA
 */
export const usePersistAuth = () => {
  useEffect(() => {
    // Prevent page unload in PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
      window.addEventListener('beforeunload', (e) => {
        // Save current state before unload
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        
        if (token && user) {
          sessionStorage.setItem('auth_backup', JSON.stringify({ token, user }));
        }
      });
    }

    // Restore auth state on mount
    const restoreAuth = () => {
      const backup = sessionStorage.getItem('auth_backup');
      if (backup) {
        try {
          const { token, user } = JSON.parse(backup);
          if (token && !localStorage.getItem('token')) {
            localStorage.setItem('token', token);
            localStorage.setItem('user', user);
          }
        } catch (error) {
          console.error('Failed to restore auth:', error);
        }
      }
    };

    restoreAuth();
  }, []);
};
